import {
  Button,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  Tag,
  Text,
} from "@chakra-ui/react";
import {
  RiArrowRightLine,
  RiChat3Line,
  RiErrorWarningLine,
  RiMailLine,
  RiTranslate2,
} from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { RawHtmlViewer } from "src/components/HtmlViewer";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { useBranding } from "src/components/Providers/BrandingProvider";
import {
  convertPlainTextToHtml,
  replaceCIDsWithInlineImages,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { useGetEnglishLanguageName } from "src/hooks/useGetEnglishLanguageName";
import { useMessageRichContentQuery } from "src/hooks/useMessageRichContent";
import { useOrganization } from "src/hooks/useOrganization";
import {
  formatIsoDateToMessageTimestamp,
  formatUSPhoneNumber,
} from "src/services/format";
import * as GQL from "src/types/graphql";
import * as EmailBody from "src/types/messages/clientBranding/emailBody";
import { RichContentDocument } from "src/types/messages/content";
import { FormattedFormMessage, getFormattedTransition } from "./helpers";

interface Props {
  formMessage: FormattedFormMessage;
}

const renderMessageStatus = (
  status: GQL.message_status_enum,
  sentAt: string | null,
  organization: ReturnType<typeof useOrganization>
) => {
  switch (status) {
    case GQL.message_status_enum.Failed:
    case GQL.message_status_enum.NotSent:
      return (
        <Flex gap={1} alignItems="center">
          <Text color="gray.300">
            <RiErrorWarningLine size="20px" />
          </Text>
          <Text fontSize="sm" fontWeight="400" color="gray.600" textAlign="end">
            Not sent
          </Text>
        </Flex>
      );
    case GQL.message_status_enum.Pending:
      return (
        <Text
          fontSize="sm"
          fontWeight="400"
          color="gray.600"
          textAlign="end"
          fontStyle="italic"
        >
          Pending...
        </Text>
      );
    case GQL.message_status_enum.Sent:
      return (
        <Text fontSize="sm" fontWeight="400" color="gray.600" textAlign="end">
          Sent {sentAt && formatIsoDateToMessageTimestamp(sentAt, organization)}
        </Text>
      );
    default:
      return null;
  }
};

const renderMessageRecipient = (
  messageType: GQL.message_type_enum,
  recipient?: string | null
) => {
  const isEmail = messageType === GQL.message_type_enum.email;
  const Icon = isEmail ? RiMailLine : RiChat3Line;
  const label = isEmail ? "Email" : "SMS";
  const recipientText = recipient
    ? `${label} to ${isEmail ? recipient : formatUSPhoneNumber(recipient)}`
    : label;

  return (
    <>
      <Text color="gray.300">
        <Icon />
      </Text>
      <Text fontSize="sm" fontWeight="400" color="gray.600">
        {recipientText}
      </Text>
    </>
  );
};

const renderEmailContent = (
  richContent: RichContentDocument | null,
  formMessage: FormattedFormMessage,
  branding: ReturnType<typeof useBranding>
) => {
  const emailHtml = EmailBody.template(
    branding,
    richContent?.html
      ? replaceCIDsWithInlineImages(richContent.html, richContent.attachments)
      : formMessage.body
      ? convertPlainTextToHtml(formMessage.body)
      : ""
  );

  return (
    <Flex direction="column" gap={4}>
      {formMessage.subject && (
        <Flex direction="column">
          <Text fontSize="sm" fontWeight="400" color="gray.600">
            Subject
          </Text>
          <Text>{formMessage.subject}</Text>
        </Flex>
      )}

      {formMessage.body && (
        <Flex direction="column">
          <Text fontSize="sm" fontWeight="400" color="gray.600">
            Body
          </Text>
          <RawHtmlViewer
            border="1px solid gray.200"
            minHeight="30rem"
            html={emailHtml}
          />
        </Flex>
      )}
    </Flex>
  );
};

const FormMessageCardSkeleton: React.FC<{ isEmail?: boolean }> = ({
  isEmail = true,
}) => {
  return (
    <Flex
      direction="column"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="blackAlpha.80"
      padding="6"
      gap={5}
    >
      <Flex gap={2} alignItems="center">
        <Flex direction="row" width="100%">
          <Skeleton height="1.5rem" width="150px" />
          <Spacer />
          <Skeleton height="2rem" width="180px" />
        </Flex>
      </Flex>

      {isEmail ? (
        <Flex direction="column" gap={4}>
          <Flex direction="column" gap={2}>
            <Skeleton height="0.875rem" width="60px" />
            <Skeleton height="1.25rem" width="200px" />
          </Flex>
          <Flex direction="column" gap={2}>
            <Skeleton height="0.875rem" width="40px" />
            <Skeleton height="30rem" width="100%" borderRadius="md" />
          </Flex>
        </Flex>
      ) : (
        <Skeleton height="4rem" width="100%" />
      )}

      <Flex justifyContent="space-between" w="100%">
        <Flex gap={1} alignItems="center">
          <Skeleton height="1.25rem" width="1.25rem" borderRadius="full" />
          <Skeleton height="1rem" width="120px" />
        </Flex>
        <Skeleton height="1rem" width="100px" />
      </Flex>
    </Flex>
  );
};

const FormMessageCardContent: React.FC<{
  formMessage: FormattedFormMessage;
  richContent: RichContentDocument | null;
}> = ({ formMessage, richContent }) => {
  const organization = useOrganization();
  const branding = useBranding();
  const isEmail = formMessage.messageType === GQL.message_type_enum.email;
  const transition = formMessage.transition
    ? getFormattedTransition(formMessage.transition)
    : null;
  const getLanguageName = useGetEnglishLanguageName();

  return (
    <Flex
      direction="column"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="blackAlpha.80"
      padding="6"
      gap={5}
    >
      <Flex gap={2} alignItems="center">
        <Flex direction="row" width="100%">
          <Heading as="h3" fontWeight={700} fontSize="md">
            {formMessage.sender}
          </Heading>
          <Spacer />
          {!!richContent?.language && (
            <Button
              variant="outline"
              size="sm"
              color="gray.700"
              fontWeight="400"
              borderColor="blackAlpha.200"
              leftIcon={<RiTranslate2 size="16px" />}
            >
              {richContent.language === "en"
                ? `Showing original English`
                : `Showing sent ${getLanguageName(
                    richContent.language
                  )} translation`}
            </Button>
          )}
        </Flex>

        {transition && (
          <Tag variant="solid" background="gray.500" fontSize="xs" gap={1}>
            Automatic: {transition.from}
            {transition.to && (
              <>
                <RiArrowRightLine />
                {transition.to}
              </>
            )}
          </Tag>
        )}
      </Flex>

      {isEmail
        ? renderEmailContent(richContent, formMessage, branding)
        : formMessage.body && <Text>{formMessage.body}</Text>}

      <Flex justifyContent="space-between" w="100%">
        <Flex gap={1} alignItems="center">
          {renderMessageRecipient(
            formMessage.messageType,
            formMessage.recipient
          )}
        </Flex>
        {renderMessageStatus(
          formMessage.status,
          formMessage.sentAt,
          organization
        )}
      </Flex>
    </Flex>
  );
};

export const FormMessageCard: React.FC<Props> = ({ formMessage }) => {
  const { remoteData: richContentRemoteData } = useMessageRichContentQuery(
    formMessage.contentId
  );
  const isEmail = formMessage.messageType === GQL.message_type_enum.email;

  // Only wrap with RemoteDataView for email messages that need rich content
  if (formMessage.contentId) {
    return (
      <RemoteDataView
        remoteData={richContentRemoteData}
        error={(e) => <GenericError error={e} />}
        loading={<FormMessageCardSkeleton isEmail={isEmail} />}
      >
        {(richContent) => (
          <FormMessageCardContent
            formMessage={formMessage}
            richContent={richContent}
          />
        )}
      </RemoteDataView>
    );
  }

  // For SMS or email without contentId, render directly
  return (
    <FormMessageCardContent formMessage={formMessage} richContent={null} />
  );
};
